import React, { useEffect, useState } from 'react';
import styled, { css, useTheme } from 'styled-components';
import { useReward } from 'react-rewards';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import Button from '../../components/button';
import { Typography } from '../../components/typography';
import LoadingSpinner from '../../components/loading-spinner';
import useRedeem from './use-redeem';
import { useDeviceInfo } from '../../retail-config/device';
import { CompVoucher, CompVoucherStatus, EntitlementDetails } from '.';
import { ButtonPlaceholderShape, ListItem } from './styles';

dayjs.extend(utc);
dayjs.extend(timezone);

const statusToButtonText: Record<CompVoucherStatus, string> = {
  ASSIGNED: 'Print',
  // The REDEEMED status (ie. printed) is presented to the end-user as Issued
  REDEEMED: 'Issued'
};

type Props = {
  disabled: boolean;
  entitlementDetails: EntitlementDetails;
  onPrintingChange(printing: boolean): void;
  onVoucherUpdated(voucher: CompVoucher): void;
  primaryIdentifier: string;
  voucher: CompVoucher;
};

export function VoucherPrintItem({
  disabled,
  entitlementDetails,
  onPrintingChange,
  onVoucherUpdated,
  primaryIdentifier,
  voucher
}: Props) {
  const { redeem, loading, error, redeemedVoucher } = useRedeem();
  const theme = useTheme();
  const { timezone } = useDeviceInfo();

  const { issuedAt, redeemedAt, status, uuid } = voucher;
  const { reward } = useReward(uuid, 'confetti');
  const [shouldBounce, setShouldBounce] = useState(false);

  const text = statusToButtonText[status];

  useEffect(() => {
    onPrintingChange(loading);
  }, [loading, onPrintingChange]);

  useEffect(() => {
    if (!loading && !error && redeemedVoucher) {
      onVoucherUpdated(redeemedVoucher);
    }
  }, [error, loading, onVoucherUpdated, redeemedVoucher]);

  useEffect(() => {
    if (showCelebration({ status, issuedAt, timezone })) {
      const delay = Math.random() * 3000;
      setTimeout(() => {
        setShouldBounce(true);
        reward();
        setTimeout(() => setShouldBounce(false), 600);
      }, delay);
    }
  }, [issuedAt, reward, status, timezone]);

  return (
    <ListItem>
      <Details
        entitlementType={entitlementDetails.entitlement}
        error={error}
        redeemedAt={redeemedAt}
        timezone={timezone}
      />
      {loading ? (
        <Printing>
          <LoadingSpinner width="2.4rem" color={theme.colors.grey500} />
          Printing...
        </Printing>
      ) : (
        <ButtonContainer id={uuid} shouldBounce={shouldBounce}>
          <PrintButton
            modifiers="primaryRound"
            text={text}
            buttonSize="standard"
            loading={loading}
            disabled={disabled || status === 'REDEEMED'}
            onClick={() => handlePrint(voucher)}
          />
        </ButtonContainer>
      )}
    </ListItem>
  );

  async function handlePrint(voucher: CompVoucher) {
    await redeem({ entitlementDetails, voucher, primaryIdentifier });
  }
}

function Details({
  entitlementType,
  error,
  redeemedAt,
  timezone
}: {
  entitlementType: string;
  error: boolean;
  redeemedAt: string | null;
  timezone: string;
}) {
  const text =
    redeemedAt && !error ? `Issued: ${formatIssuedDate({ redeemedAt, timezone })}` : 'Printing failed - try again';

  return (
    <DetailsWrapper>
      <Typography>{entitlementType}</Typography>
      {redeemedAt || error ? (
        <DetailsText error={error} size="sm" weight="medium">
          {text}
        </DetailsText>
      ) : null}
    </DetailsWrapper>
  );
}

// In the presentation layer we consider redeemedAt to be the issuedAt date
function formatIssuedDate({ redeemedAt, timezone }: { redeemedAt: string; timezone: string }) {
  return dayjs(redeemedAt).tz(timezone).format('MMM D YYYY | h:mm A');
}

const CELEBRATION_TRIGGER_MINUTES = 10;

function showCelebration({
  issuedAt,
  status,
  timezone
}: {
  issuedAt: string;
  status: CompVoucherStatus;
  timezone: string;
}): boolean {
  const issuedTime = dayjs(issuedAt).tz(timezone);
  const now = dayjs().tz(timezone);
  return status === 'ASSIGNED' && now.diff(issuedTime, 'minutes') <= CELEBRATION_TRIGGER_MINUTES;
}

const DetailsWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const DetailsText = styled(Typography)<{ error: boolean }>`
  ${({ error }) =>
    error &&
    css`
      color: ${({ theme }) => theme.colors.status.error};
    `}
`;

const PrintButton = styled(Button)<{ loading: boolean }>`
  padding: 1.1rem 2.4rem;
  &:disabled {
    background-color: ${({ theme }) => theme.colors.grey300};
    color: ${({ theme }) => theme.components.global.textColor};
    opacity: 1;
  }
  ${({ loading }) => loading && css``};
`;

const ButtonContainer = styled.div<{ shouldBounce: boolean }>`
  position: relative;
  display: inline-block;
  text-align: center;
  animation: ${({ shouldBounce }) => (shouldBounce ? 'bounce 0.6s ease-in-out' : 'none')};

  @keyframes bounce {
    0%,
    20%,
    53%,
    80%,
    100% {
      transform: translate3d(0, 0, 0);
    }
    40%,
    43% {
      transform: translate3d(0, -15px, 0);
    }
    70% {
      transform: translate3d(0, -7px, 0);
    }
    90% {
      transform: translate3d(0, -2px, 0);
    }
  }
`;

const Printing = styled(ButtonPlaceholderShape)`
  border-color: ${({ theme }) => theme.colors.grey400};
  border-style: solid;
  align-items: center;
  display: flex;
  gap: 0.5rem;
  justify-content: center;
`;
